import $ from 'jquery';
import debounce from 'lodash/debounce';
import Keyboard from './keyboard';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';

export const PLUGIN_NAME = 'horizontalScroll';
export const DEFAULTS = {
  type: '',
  stacked: false,
  persistScroll: false,
  persistScrollKey: '',
};

const BASE_SELECTOR = '.horizontal-scroll';
const WRAPPER_SELECTOR = `${BASE_SELECTOR}__wrapper`;
const SLIDES_SELECTOR = `${BASE_SELECTOR}__item`;

const isRTL = $('html').attr('dir') === 'rtl';

const isInViewport = ($img, $container) => {
  const imgTop = $img.offset().top;
  const imgBottom = imgTop + $img.outerHeight();
  const imgLeft = $img.offset().left;
  const imgRight = imgLeft + $img.outerWidth();
  const vpTop = $container.offset().top;
  const vpBottom = vpTop + $container.height();
  const vpLeft = $container.offset().left;
  const vpRight = vpLeft + $container.outerWidth();

  return imgBottom > vpTop && imgTop < vpBottom && imgLeft < vpRight && imgRight > vpLeft;
};

/**
 * Indicates whether element is within horizontal viewport.
 * @param  {HTMLElement} slide  The DOM element of the slide.
 * @return {boolean}  Whether the element is within the horizontal viewport.
 */
function isInHorizontalViewport(slide) {
  const rect = slide.getBoundingClientRect();
  const tolerance = 1;

  const LEFTMOST_VIEWPORT_BOUNDARY = 0;
  const RIGHTMOST_VIEWPORT_BOUNDARY = window.innerWidth;

  return (
    rect.left >= LEFTMOST_VIEWPORT_BOUNDARY &&
    rect.right <= RIGHTMOST_VIEWPORT_BOUNDARY + tolerance
  );
}
const addButtonsToWrapper = ($el) => {
  const previousButtonHTML = isRTL
      ? `
    <button aria-label="previous page" class="previous">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-right">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M6.25 12a.75.75 0 0 1-.51-1.3L8.648 8 5.74 5.3A.75.75 0 1 1 6.76 4.2l3.5 3.25a.75.75 0 0 1 0 1.1l-3.5 3.25a.749.749 0 0 1-.51.2z"/>
              </svg>
          </span>
        </span>
      </button>
    `
      : `
    <button aria-label="previous page" class="previous">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-left">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M9.75 12a.749.749 0 0 1-.51-.2l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 0 1 1.02 1.1L7.352 8l2.908 2.7a.75.75 0 0 1-.51 1.3z"/>
              </svg>
          </span>
        </span>
      </button>
    `;

  const nextButtonHTML = isRTL
      ? `
    <button aria-label="next page" class="next">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-left">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M9.75 12a.749.749 0 0 1-.51-.2l-3.5-3.25a.75.75 0 0 1 0-1.1l3.5-3.25a.75.75 0 0 1 1.02 1.1L7.352 8l2.908 2.7a.75.75 0 0 1-.51 1.3z"/>
              </svg>
          </span>
        </span>
      </button>
    `
      : `
    <button aria-label="next page" class="next">
        <span class="btn btn--circle">
          <span class="icon icon--sm icon--arrow-right">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path d="M6.25 12a.75.75 0 0 1-.51-1.3L8.648 8 5.74 5.3A.75.75 0 1 1 6.76 4.2l3.5 3.25a.75.75 0 0 1 0 1.1l-3.5 3.25a.749.749 0 0 1-.51.2z"/>
              </svg>
          </span>
        </span>
      </button>
    `;

  if (isRTL) {
    $el.after(nextButtonHTML);
    $el.before(previousButtonHTML);
  } else {
    $el.before(previousButtonHTML);
    $el.after(nextButtonHTML);
  }
};
const wrapHorizontalScrollContent = ($el) => {
  $el.wrap('<div class="horizontal-scroll__wrapper" aria-label="Carousel" role="region" />');
  addButtonsToWrapper($el);
};
/**
 * Updates the tabindex and aria-hidden attributes for each slide based on viewport visibility.
 * @return {void}
 */
const updateSlideState = ($el) => {
  const $slides = $el.find(SLIDES_SELECTOR);

  if (!$slides.length) return;

  $slides.each((_, slide) => {
    const tabindexValue = isInHorizontalViewport(slide) ? 0 : -1;
    const ariaHiddenValue = !isInHorizontalViewport(slide);

    $(slide).find('a:first').attr('tabindex', tabindexValue);
    $(slide).find('a:last').attr('tabindex', tabindexValue);
    $(slide).attr('aria-hidden', ariaHiddenValue);
  });
};
/**
 * Calculate combined width of all slides, whether stacked or not.
 * @param {jQuery} $slides  The slides
 * @param {Object} opts Options
 * @return {void}
 */
const calculateTotalScrollableWidth = ($slides, opts) => {
  if (opts.stacked) {
    // If stacked, calculate based on number of columns per row
    const firstSlideWidth = $slides.first().outerWidth(true);
    const visibleRows = Math.floor(
        $slides.parent().outerHeight() / $slides.first().outerHeight(true));
    const columnsPerRow = Math.ceil($slides.length / visibleRows);

    return firstSlideWidth * columnsPerRow;
  }

  // If unstacked, sum up total slides width
  return $slides.toArray().reduce(
      (totalWidth, slide) => totalWidth + $(slide).outerWidth(true), 0,
  );
};
/**
 * Updates enabled/disabled state of buttons based on current slider position.
 * @return {void}
 */
const updateButtonState = ($el, opts) => {
  const $slides = $el.find(`${BASE_SELECTOR}__item`);
  const $prev = $el.closest(WRAPPER_SELECTOR).find('.previous');
  const $next = $el.closest(WRAPPER_SELECTOR).find('.next');

  // Calculate container width and total slides width
  const containerWidth = $el.outerWidth();
  const totalScrollableWidth = calculateTotalScrollableWidth($slides, opts);

  // Calculate current scroll position and determine boundaries
  const currentScrollLeft = $el.scrollLeft();
  const lastSlideOffset = totalScrollableWidth - containerWidth;

  if (isRTL) {
    // In RTL, scrollLeft is negative as you scroll leftwards,
    // so the "end" is the most negative value
    const mostNegativeScrollLeft = -totalScrollableWidth + containerWidth;

    // Determine whether on first or last slide in RTL mode
    // Near zero in RTL mode means visual start
    const onFirstSlide = currentScrollLeft >= -1;
    // Near max negative value means visual end
    const onLastSlide = currentScrollLeft <= mostNegativeScrollLeft + 1;

    // Finally, update button state for RTL
    $prev.prop('disabled', onFirstSlide);
    $prev.attr('aria-hidden', onFirstSlide);
    $next.prop('disabled', onLastSlide);
    $next.attr('aria-hidden', onLastSlide);
  } else {
    // Determine whether on first or last slide in LTR mode
    const onFirstSlide = currentScrollLeft <= 0;
    const onLastSlide = currentScrollLeft >= lastSlideOffset;

    // Finally, update button state for LTR
    $prev.prop('disabled', onFirstSlide);
    $prev.attr('aria-hidden', onFirstSlide);
    $next.prop('disabled', onLastSlide);
    $next.attr('aria-hidden', onLastSlide);
  }
};

const showImage = ($el, key) => {
  const $lazyImages = $el.find('img[data-lazy]').not('.loaded');
  if (!$lazyImages.length) {
    $(window).off(`scroll.horizontalScroll-${key}`);
    $($el).off(`scroll.horizontalScroll-${key}`);
  }
  $lazyImages.each(function eachLazyImage() {
    const $lazyImage = $(this);
    if (isInViewport($lazyImage, $el)) {
      $lazyImage.attr('src', $lazyImage.attr('data-lazy'));
      if ($lazyImage.attr('data-srcset')) {
        $lazyImage.attr('srcset', $lazyImage.attr('data-srcset'));
      }
      $lazyImage.addClass('loaded');
    }
  });
};
const onWindowScroll = ($el, key) => {
  $(window).on(`scroll.horizontalScroll-${key}`, () => {
    showImage($el, key);
  });
};
/**
 * Animates the horizontal scroll to specified index.
 * @param  {number} index  The slide index to which to move.
 * @return {void}
 */
const moveSlider = (index, $el, $slides, opts) => {
  const isWithinBounds = index >= 0 && index < $slides.length;

  if (isWithinBounds) {
    // Calculate new scroll position based on slide width
    const slideWidth = $slides.eq(index).outerWidth(true);
    const scrollDirection = isRTL ? -1 : 1;
    const scrollPosition = slideWidth * index * scrollDirection;
    updateButtonState($el, opts);


    $el.animate({ scrollLeft: scrollPosition }, 'fast', () => {
      // After scroll animation is complete, set focus on the first focusable
      // element inside the visible slide
      const $visibleSlide = $slides.eq(index);
      $visibleSlide.find('a, button, input, [tabindex="0"]').first().focus();
    });
  }
};
/**
 * Gets index of currently visible slide.
 * @return {number}  Index of currently visible slide.
 */
const getCurrentSlideIndex = ($slides) => {
  let currentIndex = 0;

  $slides.each(function checkInView(index) {
    if (isInHorizontalViewport(this)) {
      currentIndex = index;
      // Break `each` loop after finding first visible slide
      return false;
    }
    // For ESLint
    return true;
  });

  return currentIndex;
};
/**
 * Moves slider depending on keyboard input.
 * @return {void}
 */
const handleButtonPress = (event, $el, $buttons, opts) => {
  const $slides = $el.find(`${BASE_SELECTOR}__item`);

  const key = Keyboard.parseKey(event);

  if (key === 'ENTER' || key === 'SPACE' || event.type === 'click') {
    event.preventDefault();

    const $button = $(event.target);
    const currentIndex = getCurrentSlideIndex($slides);
    let targetIndex;
    if (isRTL) {
      const hasPreviousClass = $button.hasClass('previous');
      if (hasPreviousClass) {
        targetIndex = Math.max(currentIndex - 1, 0);
      } else {
        targetIndex = Math.min(currentIndex + 1, $slides.length - 1);
      }
    } else {
      const hasPreviousClass = $button.hasClass('previous');
      if (hasPreviousClass) {
        targetIndex = currentIndex - 1;
      } else {
        targetIndex = currentIndex + 1;
      }
    }

    moveSlider(targetIndex, $el, $slides, opts);
  }
};
const onKeyDown = ($el, $a11yButtons, opts) => $a11yButtons.on('keydown', (evt) => handleButtonPress(evt, $el, $a11yButtons, opts));
const onClick = ($el, $a11yButtons, opts) => $a11yButtons.on('click', (evt) => handleButtonPress(evt, $el, $a11yButtons, opts));

const onButtonFocus = ($a11yButtons) => {
  $a11yButtons.on('focus', function removeAriaHidden() {
    $(this).attr('aria-hidden', false);
  });
};
const onButtonBlur = ($a11yButtons) => {
  $a11yButtons.on('blur', function addAriaHidden() {
    $(this).attr('aria-hidden', true);
  });
};

const scrollTrackingEvent = ($el, opts) => () => {
  const $image = $el.find('img');
  const imageIndexes = [];
  $image.each(function eachImage() {
    const $thisImage = $(this);
    const imageIndex = $thisImage.closest('.horizontal-scroll__item').index();
    if (isInViewport($thisImage, $el)) {
      imageIndexes.push(imageIndex + 1);
    }
  });
  const carouselEvent = new CustomEvent('carouselChange');
  carouselEvent.tracking = {
    type: opts.type,
    currentSlide: imageIndexes[imageIndexes.length - 1],
    pageUrl: window.location.href,
    pageTitle: document.title,
    element: $el,
  };
  window.dispatchEvent(carouselEvent);
};
const onHorizontalScroll = ($el, key, opts) => {
  const trackingScroll = scrollTrackingEvent($el, opts);
  // event listener handles the displaying of images
  $($el).on(`scroll.horizontalScroll-${key}`, () => {
    showImage($el, key);
  });
  // event listener used to track marketing tracking numbers when the users scrolls.
  $($el).on(`scroll.horizontalEventScroll-${key}`, debounce(trackingScroll, 500));
  $($el).on('scroll.horizontalScroll', () => {
    updateSlideState($el);
    updateButtonState($el, opts);
  });
};

const persistScrollPosition = ($el, storageKey) => {
  const saved = sessionStorage.getItem(storageKey);
  if (saved !== null) {
    $el.scrollLeft(parseInt(saved, 10));
  } else {
    const $active = $el.find('.active').first();
    if ($active.length) {
      const container = $el[0];
      const item = $active[0];
      container.scrollLeft =
          item.offsetLeft - container.offsetLeft - (container.offsetWidth / 2) + (item.offsetWidth / 2);
    }
  }

  $el.css('visibility', 'initial');
  $el.on('scroll.persistScroll', function () {
    sessionStorage.setItem(storageKey, $el.scrollLeft());
  });
};

const addAccessibleCarousel = ($el, opts) => {
  wrapHorizontalScrollContent($el);
  updateSlideState($el);
  updateButtonState($el, opts);
};

const addEventListeners = ($el, key, opts) => {
  const $a11yButtons = $el.closest(WRAPPER_SELECTOR).find('> button');

  onWindowScroll($el, key);
  onHorizontalScroll($el, key, opts);
  onKeyDown($el, $a11yButtons, opts);
  onClick($el, $a11yButtons, opts);
  onButtonFocus($a11yButtons);
  onButtonBlur($a11yButtons);
};

const init = ($el, opts) => {
  // key - creates unique name for the event namespace, so it can be later disabled
  const key = `key-${Math.ceil(Math.random() * 10000000000)}`;
  $el.find('img[data-lazy]').addClass('horizontal-scroll__lazy-image');
  showImage($el, key);
  addAccessibleCarousel($el, opts);
  addEventListeners($el, key, opts);
  if (opts.persistScroll && opts.persistScrollKey) {
    persistScrollPosition($el, opts.persistScrollKey);
  }
};

export default function horizontalScroll(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const showImages = init($el, opts);
  return {
    showImages: () => showImages(),
  };
}

registerJQueryPlugin(PLUGIN_NAME, horizontalScroll);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/horizontal-scroll.js